import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable, {
  Column,
} from "../../components/common/data-table/DataTable";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import styles from "./AllStores.module.scss";
import StoresService from "../../api/services/stores.service";
import { formatDate } from "../../utils/date";
import DeleteModal from "../../components/common/delete-modal/DeleteModal";
import { useAuth } from "../../context/AuthContext";
import Store from "../../assets/images/table/Store";

const AllStores = () => {
  const { t } = useTranslation("translation");
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const { searchValue } = useAuth();
  const [count, setCount] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const columns: Column[] = [
    {
      key: "name",
      header: <Store />,
      render: (_, row) => (
        <div className={styles.user_cell}>
          {row?.logo_url ? (
            <img
              src={row?.logo_url}
              alt={row?.name}
              className={styles.user_avatar}
            />
          ) : (
            <div className={styles.no_image}>
              {row?.name[0]}
              {row?.name[1]}
            </div>
          )}
          <div className={styles.user_info}>
            <p className={styles.user_name}>{row.name}</p>
            <a href={`tel:${row?.contact_phone}`} className={styles.user_phone}>
              {row?.contact_phone || ""}
            </a>
            <a href={`mailto:${row?.email}`} className={styles.user_email}>
              {row?.email}
            </a>
          </div>
        </div>
      ),
      filter: true,
    },
    { key: "description", header: t("stores.table.description") as string },
    {
      key: "business_address",
      header: t("stores.table.business_address") as string,
    },
    {
      key: "created",
      header: t("stores.table.created") as string,
      render: (d) => formatDate(d),
    },
    {
      key: "is_active",
      header: t("stores.table.status") as string,
      render: (_, row) => {
        const status = row.is_active;
        let statusClass = "";
        let label = "";
        switch (status) {
          case true:
            statusClass = styles.status_active;
            label = t("stores.status.active") as string;
            break;
          case false:
            statusClass = styles.status_deactive;
            label = t("stores.status.deactive") as string;
            break;
        }
        return (
          <span className={`${styles.status} ${statusClass}`}>{label}</span>
        );
      },
    },
  ];

  const getStores = useCallback(async () => {
    if (!searchValue) setLoading(true);
    try {
      const res = await StoresService.getALLStores(page, limit, searchValue);
      setStores(res.data.data);
      setCount(res.data.count);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchValue]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return;
    if (count && newPage > Math.ceil(count / limit)) return;
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleDelete = async (id: number) => {
    try {
      await StoresService.deleteStore(id);
      setDeleteModal(false);
      getStores();
    } catch (error) {}
  };

  useEffect(() => {
    getStores();
  }, [getStores]);

  return (
    <div className={styles.delivery}>
      <TitleUpdater title={t("stores.title")} />
      <DataTable
        table_title={t("stores.title") as string}
        data={stores}
        columns={columns}
        actions
        onDelete={(id) => {
          setDeleteModal(true);
          setDeleteId(id);
        }}
        loading={loading}
        paginationData={{
          total: count,
          page,
          limit,
          onPageChange: handlePageChange,
          onLimitChange: handleLimitChange,
        }}
      />
      <DeleteModal
        deleteModal={deleteModal}
        onClose={() => setDeleteModal(false)}
        onSubmit={() => handleDelete(Number(deleteId))}
        title={t("stores.delete_title") as string}
      />
    </div>
  );
};

export default AllStores;
